import { useEffect, useId, useState } from 'react';
import type { TutorialNavHighlightRect } from '@/context/SidebarContext';

type SiteTutorialNavSpotlightProps = {
	hole: TutorialNavHighlightRect;
};

/** Margem extra ao redor do elemento focado (px). */
const HOLE_EXPAND_PX = 10;
/** Raio das bordas do “buraco” do foco (px). */
const HOLE_BORDER_RADIUS_PX = 16;

const holeMaskTransition = {
	transition:
		'x 700ms ease-in-out, y 700ms ease-in-out, width 700ms ease-in-out, height 700ms ease-in-out, rx 700ms ease-in-out, ry 700ms ease-in-out',
} as const;

/**
 * Escurece a viewport inteira exceto a área do foco (coords em px).
 * Buraco um pouco maior que o alvo, bordas arredondadas; transição 700ms como antes.
 * Usa máscara SVG (sem mix-blend) para o recorte ficar transparente de forma confiável.
 */
const SiteTutorialNavSpotlight = ({ hole }: SiteTutorialNavSpotlightProps) => {
	const reactId = useId().replace(/:/g, '');
	const maskId = `site-tutorial-spotlight-${reactId}`;

	const [vw, setVw] = useState(() =>
		typeof window !== 'undefined' ? window.innerWidth : 0,
	);
	const [vh, setVh] = useState(() =>
		typeof window !== 'undefined' ? window.innerHeight : 0,
	);

	useEffect(() => {
		const onResize = () => {
			setVw(window.innerWidth);
			setVh(window.innerHeight);
		};
		onResize();
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	const { top, left, right, bottom } = hole;
	const clamp = (value: number, min: number, max: number) =>
		Math.max(min, Math.min(max, value));

	if (vw <= 0 || vh <= 0) return null;

	const cTop = clamp(top, 0, vh);
	const cBottom = clamp(bottom, 0, vh);
	const cLeft = clamp(left, 0, vw);
	const cRight = clamp(right, 0, vw);

	const rawW = cRight - cLeft + 2 * HOLE_EXPAND_PX;
	const rawH = cBottom - cTop + 2 * HOLE_EXPAND_PX;
	const rawLeft = cLeft - HOLE_EXPAND_PX;
	const rawTop = cTop - HOLE_EXPAND_PX;

	const holeW = Math.max(0, rawW);
	const holeH = Math.max(0, rawH);
	const holeLeft = clamp(rawLeft, 0, Math.max(0, vw - holeW));
	const holeTop = clamp(rawTop, 0, Math.max(0, vh - holeH));

	const rr = Math.min(
		HOLE_BORDER_RADIUS_PX,
		holeW > 0 ? holeW / 2 : 0,
		holeH > 0 ? holeH / 2 : 0,
	);

	return (
		<svg
			className="pointer-events-auto fixed inset-0 z-[40] h-full w-full"
			width={vw}
			height={vh}
			aria-hidden
		>
			<defs>
				<mask
					id={maskId}
					maskUnits="userSpaceOnUse"
					maskContentUnits="userSpaceOnUse"
				>
					<rect x={0} y={0} width={vw} height={vh} fill="white" />
					<rect
						fill="black"
						style={{
							x: holeLeft,
							y: holeTop,
							width: holeW,
							height: holeH,
							rx: rr,
							ry: rr,
							...holeMaskTransition,
						}}
					/>
				</mask>
			</defs>
			<rect
				x={0}
				y={0}
				width={vw}
				height={vh}
				fill="rgba(0,0,0,0.55)"
				mask={`url(#${maskId})`}
			/>
		</svg>
	);
};

export { SiteTutorialNavSpotlight };
